import { useEffect, useState, useContext, useCallback } from "react";
import DataTable from "components/utils/DataTable";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import ShopContext from "context/ShopContext";
import styles from "./styles.module.css";
import EmptyState from "components/utils/EmptyState/EmptyState";
import toast from "react-hot-toast";
import PrinterImg from "images/svg/printer.svg";
import * as ReportHelper from "helpers/ReportHelper";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ShopsHelper from "helpers/ShopsHelper";
import * as DeletedCartHelper from "helpers/DeletedCartHelper";
import * as EditedSaleHelper from "helpers/EditedSaleHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import { STORAGE_KEYS, INVOICE_FORMATS } from "const";

export default function SaleSummary({ shiftId }) {
  const { shop } = useContext(ShopContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletedCarts, setDeletedCarts] = useState([]);
  const [editedSales, setEditedSales] = useState([]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const rows = await ReportHelper.getSaleSummaryForShift({ autoSave: true, shiftId });
      const processedRows = (rows || []).map((row) => ({
        ...row,
        total: (row.payable || 0) - ((row.tax || 0) + (row.cess || 0)),
      }));
      setRows(processedRows);

      const currentShiftId = shiftId || localStorage.getItem(STORAGE_KEYS.SHIFT_ID);
      const deleted = await DeletedCartHelper.getDeletedCartsByShift(currentShiftId);
      setDeletedCarts(deleted || []);

      const edited = await EditedSaleHelper.getEditedSalesByShift(currentShiftId);
      setEditedSales(edited || []);
    } catch (error) {
      toast.error("Fetch report failed");
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const headerData = [
    {
      label: "Bill No",
      id: "bill_no",
      type: "text",
    },
    {
      label: "Date",
      id: "date",
      type: "callback",
      width: "110px",
      viewRender: (item) => DateTimeHelpers.getDate(item.time),
    },
    {
      label: "Time",
      id: "time",
      type: "callback",
      width: "100px",
      viewRender: (item) => DateTimeHelpers.getLocalTime(item.time),
    },
    {
      label: "Customer",
      id: "customer_name",
      type: "callback",
      viewRender: (item) => item?.customer?.name || "-",
    },
    {
      label: "Mobile",
      id: "customer_mobile",
      type: "callback",
      viewRender: (item) => item?.customer?.mobile || "-",
    },
    {
      label: "Discount",
      id: "discount_applied_on_items",
      type: "callback",
      viewRender: (item) =>
        ShopsHelper.getAmountFormatted(
          shop,
          (Number(item?.discount_applied_on_items) || 0) + (Number(item?.discount) || 0),
        ),
    },
    {
      label: "Net Amount",
      id: "total",
      type: "floatAmount",
    },
    {
      label: "Tax Amount",
      id: "tax",
      type: "floatAmount",
    },
    {
      label: "Additional Tax",
      id: "cess",
      type: "floatAmount",
    },
    {
      label: "Total Amount",
      id: "payable",
      type: "floatAmount",
    },
  ];

  const handlePrintSalesSummary = async () => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getSalesSummaryMultiThreeInch(rows, shop, shiftId, deletedCarts);
    } else {
      reportText = await ReportHelper.getSalesSummaryReport(rows, shop, shiftId, deletedCarts);
    }
    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  const handlePrintSalesReport = async () => {
    const terminalSettings = TerminalHelper.getTerminalSettings();
    const invoiceFormat = terminalSettings.invoicePrinterFormat || INVOICE_FORMATS.THREE_INCH;

    let reportText;
    if (
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE_ARABIC ||
      invoiceFormat === INVOICE_FORMATS.THREE_INCH_MULTI_LANGUAGE
    ) {
      reportText = await ReportHelper.getSalesReportMultiThreeInch(rows, shop, shiftId, deletedCarts);
    } else {
      reportText = await ReportHelper.getSalesReportThreeInch(rows, shop, shiftId, deletedCarts);
    }
    await ReportHelper.printReport({ reportText, fallbackFunction: window.print });
  };

  const _getSalesReportSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let totalDiscount = 0;
    let totalNetAmount = 0;

    items.forEach((item) => {
      totalPayable += item?.payable;
      totalTax += item.tax;
      totalAdditionalTax += item.cess;
      totalDiscount += (Number(item.discount_applied_on_items) || 0) + (Number(item.discount) || 0);
      totalNetAmount += item.total || 0;
    });

    return {
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
      totalNetAmount: ShopsHelper.getAmountFormatted(shop, totalNetAmount),
    };
  };

  const summary = _getSalesReportSummary(rows);
  const deletedTotals = DeletedCartHelper.computeDeletedCartTotals(deletedCarts);
  const editedTotals = EditedSaleHelper.computeEditedSaleTotals(editedSales);

  return (
    <div className={styles.container}>
      <div style={{ height: "100%" }}>
        <div className={styles.headerSection}>
          <div>
            <h2 className={styles.title}>Sale Summary</h2>
            <h3 className={styles.subTitle}>Key financials and status of the current shift</h3>
          </div>

          <div>
            <ButtonRed
              style={{ marginRight: "10px" }}
              onClick={handlePrintSalesSummary}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
            >
              Sales Summary
            </ButtonRed>
            <ButtonRed
              onClick={handlePrintSalesReport}
              startIcon={<img src={PrinterImg} alt="Print" className={styles.iconSmall} />}
            >
              Sales Report
            </ButtonRed>
          </div>
        </div>
        {loading ? (
          <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
        ) : rows.length > 0 ? (
          <>
            <DataTable
              columns={headerData}
              rows={rows}
              rowKey="id"
              summary={{
                bill_no: "Total",
                tax: `${shop?.currency || ""} ${summary.totalTax}`,
                cess: `${shop?.currency || ""} ${summary.totalAdditionalTax}`,
                discount_applied_on_items: `${shop?.currency || ""} ${summary.totalDiscount}`,
                total: `${shop?.currency || ""} ${summary.totalNetAmount}`,
                payable: `${shop?.currency || ""} ${summary.totalPayable}`,
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "24px",
                padding: "12px 16px",
                borderTop: "1px solid #e0e0e0",
                marginTop: "8px",
                background: "#fff8f8",
                borderRadius: "0 0 8px 8px",
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontWeight: 600, color: "#555" }}>
                Total Carts Deleted: <span style={{ color: "#e50218" }}>{deletedTotals.count}</span>
              </span>
              <span style={{ fontWeight: 600, color: "#555" }}>
                Deleted Carts Value:
                <span style={{ color: "#e50218" }}>
                  {shop?.currency || ""} {ShopsHelper.getAmountFormatted(shop, deletedTotals.totalAmount)}
                </span>
              </span>
              <span style={{ fontWeight: 600, color: "#555", marginLeft: "12px" }}>
                Total Sales Edited: <span style={{ color: "#e50218" }}>{editedTotals.count}</span>
              </span>
              <span style={{ fontWeight: 600, color: "#555" }}>
                Edited Sales Value:
                <span style={{ color: "#e50218" }}>
                  {shop?.currency || ""} {ShopsHelper.getAmountFormatted(shop, editedTotals.totalAmount)}
                </span>
              </span>
            </div>
          </>
        ) : (
          <EmptyState title="Sales summary" />
        )}
      </div>
    </div>
  );
}
